import React, { useState } from 'react';
import { Upload, message } from 'antd';
import { InboxOutlined, LoadingOutlined } from '@ant-design/icons';
import { useSession } from 'next-auth/react';
import {
  useCreateOneUserUploadedAssetsMutation,
  FindManyUserUploadedAssetsDocument,
} from '@src/components/generated/graphql';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';
import styles from './video-uploader.module.css';

const { Dragger } = Upload;

const VIDEO_EXTENSIONS = ['mp4', 'mov', 'webm'] as const;
const ACCEPTED_VIDEO_EXTENSIONS = '.mp4, .mov, .webm';
const MAX_FILE_SIZE_MB = 100;
const FILE_TYPE = 'assets';

const MESSAGES = {
  INVALID_FILE_TYPE: 'You can only upload MP4, MOV, or WEBM files!',
  FILE_TOO_LARGE: `Video must be smaller than ${MAX_FILE_SIZE_MB}MB!`,
  NOT_SIGNED_IN: 'Please sign in to upload videos',
  UPLOAD_SUCCESS: 'Video uploaded successfully and added to inventory!',
  UPLOAD_ERROR: 'Failed to upload video. Please try again.',
  UPLOAD_ERROR_SERVER: 'Error when uploading video',
} as const;

const UPLOAD_BUTTON_TEXT = {
  DEFAULT: 'Click or drag video to upload',
  UPLOADING: 'Uploading...',
  HINT: `Support for MP4, MOV, WEBM files (max ${MAX_FILE_SIZE_MB}MB)`,
} as const;

interface VideoUploaderProps {
  onVideoUploaded: (videoUrl: string) => void;
  disabled?: boolean;
}

type VideoExtension = typeof VIDEO_EXTENSIONS[number];
type UploadResponse = {
  status: boolean;
  error?: string;
};

const getFileExtension = (filename: string): string | undefined => {
  return filename.split('.').pop()?.toLowerCase();
};

const isValidVideoFile = (file: File): boolean => {
  const extension = getFileExtension(file.name);
  return extension
    ? VIDEO_EXTENSIONS.includes(extension as VideoExtension)
    : false;
};

const isFileSizeValid = (file: File): boolean => {
  return file.size / 1024 / 1024 < MAX_FILE_SIZE_MB;
};

const uploadVideo = async (file: File, creator: string): Promise<string> => {
  const data = new FormData();
  data.set('file', file);
  data.set('fileName', file.name);
  data.set('email', creator);
  data.set('fileType', FILE_TYPE);

  try {
    const response = await fetch('/api/upload-file-s3', {
      method: 'POST',
      body: data,
    });

    const result: UploadResponse = await response.json();

    if (!result?.status) {
      throw new Error(MESSAGES.UPLOAD_ERROR_SERVER);
    }

    const CDN_URL = process.env.NEXT_PUBLIC_ASSETS_IMAGES_URL;
    if (!CDN_URL) {
      throw new Error('CDN URL is not configured');
    }

    return `${CDN_URL}/${encodeURIComponent(
      creator
    )}/${FILE_TYPE}/${encodeURIComponent(file.name)}`;
  } catch (error) {
    console.error('Upload error:', error);
    throw new Error(MESSAGES.UPLOAD_ERROR);
  }
};

const VideoUploader: React.FC<VideoUploaderProps> = ({
  onVideoUploaded,
  disabled = false,
}) => {
  const { data: session } = useSession();
  const [loading, setLoading] = useState<boolean>(false);
  const [createInventoryItem] = useCreateOneUserUploadedAssetsMutation();

  const beforeUpload = (file: File): boolean => {
    if (!isValidVideoFile(file)) {
      message.error(MESSAGES.INVALID_FILE_TYPE);
      return false;
    }

    if (!isFileSizeValid(file)) {
      message.error(MESSAGES.FILE_TOO_LARGE);
      return false;
    }

    return true;
  };

  const handleUpload = async (file: File): Promise<void> => {
    if (!session?.user?.email) {
      message.error(MESSAGES.NOT_SIGNED_IN);
      return;
    }

    setLoading(true);

    try {
      const videoUrl = await uploadVideo(file, session.user.email);
      const assetName = file.name.split('.')[0];

      await createInventoryItem({
        variables: {
          data: {
            assetType: ASSET_TYPE.PRIMITIVE_TEXTURE.VIDEO_PLANE as any,
            assetUrl: videoUrl,
            name: assetName,
            userId: session.user.id!,
          },
        },
        refetchQueries: [FindManyUserUploadedAssetsDocument],
      });

      onVideoUploaded(videoUrl);
      message.success(MESSAGES.UPLOAD_SUCCESS);
    } catch (error) {
      console.error('Upload error:', error);
      message.error(MESSAGES.UPLOAD_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const uploadButton = (
    <div className={styles.uploadContent}>
      <p className='ant-upload-drag-icon'>
        {loading ? <LoadingOutlined /> : <InboxOutlined />}
      </p>
      <p className='ant-upload-text'>
        {loading ? UPLOAD_BUTTON_TEXT.UPLOADING : UPLOAD_BUTTON_TEXT.DEFAULT}
      </p>
      <p className='ant-upload-hint'>{UPLOAD_BUTTON_TEXT.HINT}</p>
    </div>
  );

  return (
    <div className={styles.videoUploader}>
      <Dragger
        name='video'
        multiple={false}
        showUploadList={false}
        accept={ACCEPTED_VIDEO_EXTENSIONS}
        beforeUpload={beforeUpload}
        customRequest={({ file }) => handleUpload(file as File)}
        disabled={disabled || loading}
        className={styles.dragger}
      >
        {uploadButton}
      </Dragger>
    </div>
  );
};

export default VideoUploader;
